
import React from 'react';
import { Card } from '@/components/ui/card';
import { Teacher } from '@/types/teacher.types';
import { TeacherDetailsPaymentSection } from '../TeacherDetailsPaymentSection';
import { format } from 'date-fns';

interface TeacherSalaryTabProps {
  teacher: Teacher;
  teacherId: string;
}

export const TeacherSalaryTab = ({ teacher, teacherId }: TeacherSalaryTabProps) => {
  // Get the latest payment info from localStorage
  const getLatestPaymentInfo = () => {
    try {
      const paymentsKey = `teacher_payments_${teacherId}`;
      const existingPayments = JSON.parse(localStorage.getItem(paymentsKey) || '[]');
      if (existingPayments.length > 0) {
        // Sort payments by date (newest first)
        const sortedPayments = [...existingPayments].sort((a, b) => 
          new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        
        // Check if there's a payment for the current month
        const currentMonth = format(new Date(), 'MMMM yyyy');
        const currentMonthPayment = sortedPayments.find(p => 
          p.paymentPeriod === currentMonth || 
          (p.date && format(new Date(p.date), 'MMMM yyyy') === currentMonth)
        );
        
        return {
          lastPaymentDate: sortedPayments[0].date,
          paymentFrequency: 'Monthly',
          hasCurrentMonthPayment: !!currentMonthPayment,
          currentPaymentStatus: currentMonthPayment ? 'Paid' : 'Pending'
        };
      }
      return {
        lastPaymentDate: null,
        paymentFrequency: 'Monthly',
        hasCurrentMonthPayment: false,
        currentPaymentStatus: 'Pending'
      };
    } catch (error) {
      console.error("Error getting latest payment info:", error);
      return {
        lastPaymentDate: null,
        paymentFrequency: 'Monthly',
        hasCurrentMonthPayment: false,
        currentPaymentStatus: 'Pending'
      };
    }
  };

  const latestPaymentInfo = getLatestPaymentInfo();

  return (
    <>
      <h3 className="text-xl font-semibold mb-4">Salary Information</h3>
      <Card className="p-4 border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Base Salary</p>
            <p className="font-medium">{teacher.salary ? `${teacher.salary} AFN` : 'Not specified'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Payment Status</p>
            <p className="font-medium">
              <span className={`px-2 py-1 rounded text-sm ${
                latestPaymentInfo.currentPaymentStatus === 'Paid' ? 'bg-green-100 text-green-800' :
                latestPaymentInfo.currentPaymentStatus === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {latestPaymentInfo.currentPaymentStatus}
              </span>
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Last Payment Date</p>
            <p className="font-medium">
              {latestPaymentInfo.lastPaymentDate ? 
                format(new Date(latestPaymentInfo.lastPaymentDate), 'PPP') : 
                'Not available'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Payment Frequency</p>
            <p className="font-medium">{latestPaymentInfo.paymentFrequency}</p>
          </div>
        </div>
      </Card>

      <TeacherDetailsPaymentSection 
        teacherId={teacherId || ''} 
        teacherName={`${teacher.firstName} ${teacher.lastName}`}
        salary={Number(teacher.salary) || 0}
      />
    </>
  );
};
